import React from 'react';
import Immutable from 'immutable';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { CoreLink } from '@uncinc/react-drupal-core';
import { useTranslation } from 'react-i18next';
import type { NodeFAQ } from '../../../types/uncinc_profile/nodes';
import NodeFaq from '../../Node/Faq';
import groupNodeFaqPerTheme from './helpers/groupNodeFaqPerTheme';
import useActiveTheme from './helpers/useActiveTheme';
import useScrollToFaq from './helpers/useScrollToFaq';
import ViewFaqMenu from './Menu';

import './style.scss';

export interface ThemeGroup {
  themeId: number;
  themeName: string;
  themeUrl?: string;
  faqs: NodeFAQ[];
}

interface FaqProps {
  entities: ImmutableEntity<NodeFAQ[]>;
}

const Faq: React.FC<FaqProps> = ({ entities }) => {
  const { pathname, hash } = useLocation();
  const { t } = useTranslation();

  const themes = groupNodeFaqPerTheme(entities);
  const activeThemeId = useActiveTheme(themes);

  useScrollToFaq(hash);

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${window.location.origin}${pathname}`,
    mainEntity: entities.toJS().map((node) => ({
      '@type': 'Question',
      name: node.title[0].value,
      acceptedAnswer: {
        '@type': 'Answer',
        text: node.body[0].processed,
      },
    })),
  });

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {schema}
        </script>
      </Helmet>

      <div className="ViewFaq">
        <Container>
          <div className="row">
            <div className="col-12 col-md-5 order-md-2 col-lg-4 offset-lg-1">
              <ViewFaqMenu
                items={themes.map(({ themeId, themeName }) => ({
                  id: themeId,
                  link: `#theme-${themeId}`,
                  title: themeName,
                  isActive: themeId === activeThemeId,
                }))}
              />
            </div>

            <div className="col-12 col-md-7 order-md-1">
              <ol className="ViewFaq__themes">
                {themes.map(({
                  themeId, themeName, themeUrl, faqs,
                }) => (
                  <li
                    key={`theme:${themeId}`}
                    id={`theme-${themeId}`}
                  >
                    <div className="ViewFaq__theme">
                      <div className="ViewFaq__theme__title-wrapper">
                        <h2 className="ViewFaq__theme__title">
                          {themeName}
                        </h2>
                        {themeUrl && (
                          <CoreLink href={themeUrl}>
                            {t('views.faq.more_about_theme')}
                          </CoreLink>
                        )}
                      </div>

                      <ol className="ViewFaq__theme__faqs">
                        {faqs.map((faq) => {
                          const nid = faq.nid[0].value;
                          const langcode = faq.langcode[0].value;

                          return (
                            <li key={`faq:${nid}:${langcode}`}>
                              <NodeFaq
                                entity={Immutable.fromJS(faq) as never as ImmutableEntity<NodeFAQ>}
                                showLabel={false}
                                viewMode="inline"
                              />
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};

export default Faq;
